import { Alert, DialogActions, DialogContent, Grid, Select, MenuItem, FormControl, Button, TextField, SelectChangeEvent } from '@mui/material';
import {
    DesktopDatePickerCustom,
    DialogCustom, DialogTitleCustom, FetchAutocomplete, FieldWrap,
} from '@tap/mui-ui';
import { MdEditor } from '@tap/mui-ui';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ApiTimeTrackingLog, IApiUserAccount, LeftTimeMode, SpentTimeMode, timeTrackingService, userService } from '@tap/data-providers';
import { ICancelDialogResult, IDialogProps, useSession } from '@tap/tap-ui';
import { useDocument } from '../../context';
import { dateTimeHelper } from '@tap/utils';

/**
 *  Диалог логирования времени
 */

export enum TimeTrackingManageMode {
    Create,
    Edit,
    Delete,
}

type DialogInputParams = {
    dialogMode: TimeTrackingManageMode.Create,
    documentKey: string,
    item?: never,
} | {
    dialogMode: TimeTrackingManageMode.Edit | TimeTrackingManageMode.Delete,
    documentKey: string,
    item: ApiTimeTrackingLog,
}

export type TimeTrackingManageDialogProps = IDialogProps<DialogInputParams, ICancelDialogResult>;

export type TimeTrackingAddTimeForm = {
    spentTimeStr: string,
    startDate: Date,
    spentTimeMode: SpentTimeMode,
    leftTimeMode: LeftTimeMode,
    leftTimeStr: string,
    description: string,
    worker: IApiUserAccount | null,
    serviceData: any,
    relatedDocumentKeys: string[],
}

const todayDate = new Date();

export const TimeTrackingManageDialog = ({
    data,
    isOpen,
    handleClose,
}: TimeTrackingManageDialogProps) => {

    const { user } = useSession();

    const { componentProps } = useDocument();
    const externalProps = componentProps?.timeTrackingProps?.timeTrackingManageDialogProps || {};

    const handleCancel = () => {
        handleClose({ isCancel: true });
    }

    const formik = useFormik<TimeTrackingAddTimeForm>({
        validateOnMount: true,
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true,
        initialValues: {
            spentTimeStr: data.item ? dateTimeHelper.timeFormat(data.item.spentMin * 1000 * 60, { withSec: false, hideDays: false, hideZeroHours: false }) : '',
            startDate: data.item ? new Date(data.item.startAtUtc) : todayDate,
            spentTimeMode: externalProps.defaultSpentTimeMode || SpentTimeMode.Reduce,
            leftTimeMode: data.item?.leftTimeMode || externalProps.defaultLeftTimeMode || LeftTimeMode.Reduce,
            leftTimeStr: data.item ? dateTimeHelper.timeFormat(data.item.spentMin * 1000 * 60, { withSec: false, hideDays: false, hideZeroHours: false }) : '',
            description: data.item?.description || '',
            worker: data.item?.worker || user?.userInfo,
            serviceData: data.item?.serviceData,
            relatedDocumentKeys: data.item?.relatedDocumentKeys || [],
        },
        validationSchema: Yup.object({
            spentTimeStr: Yup.string()
                .required('Укажите затраченное время'),
            startDate: Yup.date()
                .required('Заполните дату начала.'),
            leftTimeMode: Yup.string()
                .required('Выберите как изменится оставшееся время.'),
        }),

        onSubmit: async (values) => {
            const dataParams = {
                spentMin: dateTimeHelper.timeTrackingStrToMin(values.spentTimeStr),
                startAtUtc: values.startDate,
                spentTimeMode: values.spentTimeMode,
                leftTimeMode: values.leftTimeMode,
                leftMin: dateTimeHelper.timeTrackingStrToMin(values.leftTimeStr),
                description: values.description,
                workerId: values.worker?.id,
                serviceData: values.serviceData,
                relatedDocumentKeys: values.relatedDocumentKeys,
            }
            if (data.dialogMode === TimeTrackingManageMode.Create) {
                await timeTrackingService.createTimeTrackingLog(data.documentKey, dataParams);
            } else if (data.dialogMode === TimeTrackingManageMode.Edit) {
                await timeTrackingService.updateTimeTrackingLog(data.item.id, dataParams);
            } else if (data.dialogMode === TimeTrackingManageMode.Delete) {
                await timeTrackingService.deleteTimeTrackingLog(data.item.id, dataParams);
            }

            handleClose({ isCancel: false });
        },
    });

    const { values, touched, errors } = formik;

    const handleNewCommentOnChange = ({ text }: any) => {
        formik.setFieldValue('description', text);
    }

    return (
        <DialogCustom
            handleClose={handleCancel}
            open={isOpen}
        >

            <DialogTitleCustom
                primaryText={{
                    [TimeTrackingManageMode.Create]: 'Вести журнал работ',
                    [TimeTrackingManageMode.Edit]: 'Редактировать журнал работ',
                    [TimeTrackingManageMode.Delete]: 'Удалить журнал работ',
                }[data.dialogMode]}
            />

            <DialogContent>

                {data.dialogMode === TimeTrackingManageMode.Delete && (
                    <Alert severity='info'
                        variant='standard'
                        sx={{ mb: 2 }}>
                        Вы действительно хотите удалить этот журнал работ?
                    </Alert>
                )}



                {data.dialogMode !== TimeTrackingManageMode.Delete && (
                    <>
                        <FieldWrap label={'Затраченное время'}>
                            <TextField
                                size='small'
                                name='spentTimeStr'
                                value={values.spentTimeStr}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={touched.spentTimeStr && Boolean(errors.spentTimeStr)}
                                helperText={touched.spentTimeStr && errors.spentTimeStr}
                            />
                        </FieldWrap>

                        <FieldWrap label={'Дата начала'}>
                            <DesktopDatePickerCustom
                                value={values.startDate}
                                onChange={(val: Date | null) => {
                                    formik.setFieldValue('startDate', val);
                                }}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                    },
                                }}
                            />
                        </FieldWrap>
                    </>
                )}

                {!externalProps.hideLeftTimeMode && (
                    <FieldWrap label={'Оставшееся время'}>
                        <Grid container alignItems='center'>
                            <Select
                                value={values.leftTimeMode}
                                variant='standard'
                                onChange={(e: SelectChangeEvent<LeftTimeMode>) => {
                                    formik.setFieldValue('leftTimeMode', e.target.value);
                                }}
                                sx={{ mr: 1, marginBottom: '-5px' }}
                            >
                                <MenuItem value={LeftTimeMode.Reduce}>{data.dialogMode !== TimeTrackingManageMode.Delete ? 'Уменьшить автоматически' : 'Увеличить автоматически'}</MenuItem>
                                <MenuItem value={LeftTimeMode.Keep}>Оставить без изменения</MenuItem>
                                <MenuItem value={LeftTimeMode.SetNewValue}>Установить в</MenuItem>
                            </Select>

                            {values.leftTimeMode === LeftTimeMode.SetNewValue && (
                                <TextField
                                    size='small'
                                    name='leftTimeStr'
                                    value={values.leftTimeStr}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={touched.leftTimeStr && Boolean(errors.leftTimeStr)}
                                    helperText={touched.leftTimeStr && errors.leftTimeStr}
                                />
                            )}
                        </Grid>
                    </FieldWrap>

                )}
                {true && (
                    <FieldWrap label={'Исполнитель'}>
                        <FetchAutocomplete
                            fetchRequest={({ searchText }) => {
                                return userService.getWorkerList({ searchText }).then((res) => res);
                            }}
                            value={values.worker}
                            onChange={(event: any, newValue: IApiUserAccount | null) => {
                                formik.setFieldValue('worker', newValue);
                            }}
                            textFieldProps={{
                                name: 'worker',
                            }}
                            getOptionLabel={(option) => userService.getUserFIO(option)}
                            noOptionsText={'Пользователь не найден'}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                    </FieldWrap>
                )}

                {!!externalProps && externalProps.ServiceDataFields && (
                    <externalProps.ServiceDataFields formik={formik} />
                )}


                <FieldWrap label={'Комментарий'}>
                    <FormControl sx={{ width: '100%' }}>
                        <MdEditor
                            onChange={handleNewCommentOnChange}
                            value={values.description}
                        />
                    </FormControl>
                </FieldWrap>

            </DialogContent>

            <DialogActions>
                <Grid container justifyContent='flex-end'>

                    <LoadingButton
                        loading={formik.isSubmitting}
                        disableElevation
                        type='button'
                        variant='contained'
                        color='primary'
                        sx={{ mr: 2 }}
                        onClick={formik.submitForm}
                        disabled={!formik.isValid}
                    >
                        {{
                            [TimeTrackingManageMode.Create]: 'Записать',
                            [TimeTrackingManageMode.Edit]: 'Сохранить',
                            [TimeTrackingManageMode.Delete]: 'Удалить',
                        }[data.dialogMode]}
                    </LoadingButton>

                    <Button
                        disableElevation
                        type='button'
                        variant='text'
                        color='primary'
                        onClick={handleCancel}
                    >
                        Отмена
                    </Button>

                </Grid>
            </DialogActions>

        </DialogCustom >
    );
}
